// src/hooks/useChatbot.ts
import { useEffect, useState, useCallback } from 'react';
import { useChatbot } from '../contexts/ChatbotContext';
import { supabase } from '../lib/supabase';
import { generateChatResponse } from '../services/openai';
import { useConversationContext } from './useConversationContext';
import { useRateLimiting } from './useRateLimiting';
import { useSubscription } from './useSubscription';
import { useChatbotLimits } from './useChatbotLimits';
import { useCustomTexts } from './useCustomTexts';
import { useChatMessages } from './useChatMessages';

export function useChatAPI() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { checkRateLimit } = useRateLimiting();
  const { getFormattedContext } = useConversationContext();
  const { subscription, initializeSubscription, initialized } = useSubscription();
  const { chatbotPublicId, chatbotUserId } = useChatbot();
  
  const { checkConversationLimit, initialized: limitsInitialized, fetchLimits } = useChatbotLimits();

  useEffect(() => {
    if (!initialized) {
      initializeSubscription(chatbotUserId ?? undefined);
    }
    
    if (chatbotPublicId && !limitsInitialized) {
      fetchLimits(chatbotPublicId);
    }
  }, [initialized, initializeSubscription, chatbotUserId, limitsInitialized, fetchLimits, chatbotPublicId]);

  const sendMessage = async (message: string) => {
    try {
      setLoading(true);
      setError(null);
      // Réinitialiser l'état de limite atteinte au début d'une requête
      useChatMessages.getState().setLimitReached(false);

      // Vérifier si on est en mode public
      const isPublicMode = !!chatbotPublicId && (!chatbotUserId || chatbotUserId === chatbotPublicId);

      // En mode public, on n'a pas besoin de subscription
      if (!isPublicMode && !subscription) {
        throw new Error('Subscription is not available');
      }

      // En mode public, ne pas exiger chatbotUserId
      if (!isPublicMode && !chatbotUserId) {
        throw new Error('Chatbot user ID is not available');
      }

      // Vérifier le rate limit si on n'est pas en mode public
      // En mode public on skip cette vérification
      if (!isPublicMode) {
        const isAllowed = await checkRateLimit('chat');
        console.log('isAllowed', isAllowed);
        if (!isAllowed) {
          throw new Error('Limite de requêtes atteinte. Veuillez réessayer dans quelques instants.');
        }
      }

      // Compter les conversations
      if (chatbotPublicId) {
        try {
          // Pour le mode public ou privé, compter les conversations du chatbot
          const { count, error } = await supabase
            .from('conversations')
            .select('*', { count: 'exact', head: true })
            .eq('chatbot_public_id', chatbotPublicId)
            .gte('created_at', new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString());
          
          // Vérifier la limite avec le hook
          const conversationCount = count || 0;
          const isUnderLimit = checkConversationLimit(conversationCount);
          
          if (!isUnderLimit) {
            // Définir l'état isLimitReached à true pour afficher le message d'erreur dans l'UI
            useChatMessages.getState().setLimitReached(true);
            // Utiliser le message d'erreur personnalisé depuis les design settings
            const { errorMessage } = useCustomTexts.getState();
            throw new Error(errorMessage);
          }
        } catch (err) {
          console.error("Erreur lors du comptage des conversations:", err);
          // Continuer même en cas d'erreur pour éviter de bloquer les utilisateurs
        }
      }

      // Récupérer le contexte complet de la conversation
      const conversationContext = getFormattedContext();
      if (!chatbotPublicId) {
        throw new Error('Chatbot public ID is not available');
      }
      // Envoyer le message avec le contexte complet et le chatbotPublicId
      return await generateChatResponse(message, conversationContext, chatbotPublicId);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Une erreur est survenue';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    sendMessage,
    loading,
    error
  };
}